/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Main from "../../components/HOC/Main";
import {
  fetchUserChats,
  fetchUsers,
  selectUserChatLoading,
  selectUserChats,
  selectUsers,
  selectUsersLoading,
} from "../../store/slices/api/inner/chatSlice";
import { socket } from "../../socket";
import { useLocation, useNavigate } from "react-router-dom";
import { selectLoggedInData } from "../../store/slices/api/auth/loginSlice";
import { MdOutlineClose } from "react-icons/md";

export const ChatsPage = () => {
  const dispatch = useDispatch();
  const loggedInData = useSelector(selectLoggedInData);
  const userChats = useSelector(selectUserChats);
  const chatLoading = useSelector(selectUserChatLoading);
  const [userTyping, setUserTyping] = useState(false);
  const typingTimeoutRef = useRef(null);
  const navigate = useNavigate();
  const [selectedUser, setSelectedUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState("");
  const chatRef = useRef(null);
  // Handling redirect from notifications
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const userIdFromQuery = searchParams.get("user_id");
  const isAnonymousFromQuery = searchParams.get("isAnonymous") === "true";

  const users = useSelector(selectUsers);
  const usersLoading = useSelector(selectUsersLoading);

  useEffect(() => {
    dispatch(fetchUsers());
  }, [dispatch]);

  useEffect(() => {
    chatRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, userTyping]);

  useEffect(() => {
    if (userIdFromQuery && users.length > 0) {
      if (!isAnonymousFromQuery) {
        const userToChat = users.find(
          (user) => String(user.user_id) === userIdFromQuery
        );
        if (userToChat) {
          openChat(userToChat);
        }
      } else {
        const userToChat = {
          user: { name: `Anonymous_${userIdFromQuery}`, email: null },
          user_id: Number(userIdFromQuery),
          isAnonymous: isAnonymousFromQuery,
        };
        openChat(userToChat);
      }
    }
  }, [userIdFromQuery, users]);

  useEffect(() => {
    if (userChats.length > 0) {
      setMessages(userChats);
    }
  }, [userChats]);

  const handleUserChange = (user) => {
    if (!user) return;
    if (selectedUser && selectedUser?.user_id === user.user_id) return;

    // Close current chat before switching
    if (selectedUser) {
      closeChat(selectedUser);
    }

    setSelectedUser(user);
    openChat(user);

    // update url with selected user
    navigate(`?user_id=${user.user_id}&isAnonymous=${user.isAnonymous}`, {
      replace: true,
    });
  };

  const openChat = async (user) => {
    setSelectedUser(user);
    setMessages([]);

    // Fetch chat messages of opened chat
    dispatch(
      fetchUserChats({ userId: user.user_id, isAnonymous: user.isAnonymous })
    );

    socket.emit("adminJoined", { user_id: user.user_id });
  };
  useEffect(() => {
    if (loggedInData?.authUserData) {
      const { admin_id } = loggedInData?.authUserData;
      socket.emit("joinRoom", { user_id: admin_id, is_admin: true });
    }

    socket.on("receiveMessage", (newMessage) => {
      setMessages((prev) => [...prev, newMessage]);
    });

    return () => {
      socket.off("receiveMessage");

      // Clear any existing typing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [loggedInData]);

  useEffect(() => {
    socket.on("userTypingStatus", ({ user_id, isTyping }) => {
      if (selectedUser && selectedUser.user_id === Number(user_id)) {
        setUserTyping(isTyping);
      }
    });
    return () => {
      socket.off("userTypingStatus");
    };
  }, [selectedUser]);

  const sendMessage = () => {
    if (!selectedUser || message.trim() === "") return;
    socket.emit("sendAdminMessage", {
      user_id: selectedUser.user_id,
      message,
      message_type: "text",
      isAnonymousUser: isAnonymousFromQuery,
    });

    setMessage("");

    // Clear typing indicator when sending a message
    socket.emit("adminTyping", {
      user_id: selectedUser.user_id,
      isTyping: false,
    });
  };

  const handleInputChange = (e) => {
    setMessage(e.target.value);

    if (selectedUser) {
      socket.emit("adminTyping", {
        user_id: selectedUser.user_id,
        isTyping: true,
      });

      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }

      // new timeout to stop typing indicator after 1 second of inactivity
      typingTimeoutRef.current = setTimeout(() => {
        socket.emit("adminTyping", {
          user_id: selectedUser.user_id,
          isTyping: false,
        });
      }, 1000);
    }
  };

  const closeChat = (user) => {
    if (!user) navigate("/chats");
    if (user) socket.emit("adminLeft", { user_id: user.user_id });
    setSelectedUser(null);
    setMessages([]);
    // Remove `user_id` from URL query params
    navigate("/chats");
  };
  return (
    <Main crumbs={[{ title: "masters" }, { title: "chats" }]}>
      <h1 className="inline-flex items-center mb-4">
        <p className="inline-flex font-semibold items-center text-2xl text-gray-700">
          Welcome to Chats 🎊
        </p>
      </h1>

      <div className="flex min-h-[60vh] bg-gray-100 rounded-lg">
        {/* Sidebar - Users List */}
        <div className="w-1/4 bg-[rgba(249,176,60,255)] p-4 text-black rounded-lg">
          <h2 className="text-lg font-bold mb-3">Users</h2>
          {usersLoading ? (
            <div className="space-y-2">
              {[...Array(5)].map((_, i) => (
                <div
                  key={i}
                  className="h-10 bg-gray-200 rounded-md animate-pulse"
                ></div>
              ))}
            </div>
          ) : users.length === 0 ? (
            <p>No active chats from users.</p>
          ) : (
            users.map((user) => (
              <button
                key={user.user_id}
                className={`w-full p-2 mb-2 rounded-md cursor-pointer ${
                  selectedUser && selectedUser.user_id === user.user_id
                    ? "bg-gray-100 text-black font-semibold"
                    : "text-gray-600 hover:bg-gray-100 bg-yellow-200"
                }`}
                onClick={() => handleUserChange(user)}
              >
                <div className="flex justify-between items-center">
                  <span className="text-wrap hyphens-auto break-words">
                    {user.is_anonymous
                      ? `${user.user?.name}`
                      : `${user.user?.name} ${user.user?.surname}`}
                  </span>
                  <span className="text-xs text-gray-700">
                    {user.lastActive}
                  </span>
                </div>
              </button>
            ))
          )}
        </div>

        {/* Chat Window */}
        <div className="flex flex-grow flex-col p-4">
          {selectedUser ? (
            <>
              <div className="flex justify-between items-center mb-2 w-full">
                <h2 className="text-lg font-bold">
                  Chat with{" "}
                  {selectedUser.isAnonymous
                    ? `${selectedUser.user?.name}`
                    : `${selectedUser.user.name} ${selectedUser.user.surname}`}
                </h2>
                <MdOutlineClose
                  className="cursor-pointer "
                  size={25}
                  onClick={() => closeChat(selectedUser)}
                />
              </div>
              {/* Chat Messages */}
              <div className="border p-4 flex-grow overflow-y-auto h-96 shadow-md space-y-4 bg-gray-50 rounded-xl mt-4">
                {chatLoading ? (
                  <p className="text-center text-gray-500">
                    Loading messages...
                  </p>
                ) : messages.length === 0 ? (
                  <p className="text-gray-500">No messages yet.</p>
                ) : (
                  messages.map((msg, index) => (
                    <div
                      key={index}
                      className={`p-2 my-1 text-sm rounded-lg max-w-[80%] text-wrap break-words hyphens-auto ${
                        msg.isAdmin || msg.message_by === "admin"
                          ? "bg-[rgb(249,176,60)] text-white self-end ml-auto"
                          : "bg-gray-300 text-black self-start mr-auto"
                      }`}
                    >
                      {msg.message_by === "bot" && <strong>Bot: </strong>}
                      {msg.message}
                    </div>
                  ))
                )}
                {userTyping && (
                  <div className="p-2 my-1 text-sm rounded-lg max-w-[80%] bg-gray-200 text-black self-start mr-auto animate-pulse">
                    <strong>{selectedUser.user.name} </strong>is typing...
                  </div>
                )}
                <div ref={chatRef}></div>
              </div>

              {/* Message Input */}
              <div className="mt-3 flex">
                <input
                  className="border p-2 flex-grow rounded-md"
                  type="text"
                  value={message}
                  onChange={handleInputChange}
                  placeholder="Type a message..."
                  onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                />
                <button
                  className="bg-[rgba(249,176,60,255)] text-white px-4 ml-2 rounded-md hover:bg-opacity-90"
                  onClick={sendMessage}
                >
                  Send
                </button>
              </div>
            </>
          ) : (
            <div className="p-12 flex flex-col items-center justify-start h-full text-center text-gray-600">
              <h3 className="text-lg font-semibold">No User Selected</h3>
              <p className="text-sm text-gray-600 mt-1">
                Select a user from the sidebar to start a conversation.
              </p>
            </div>
          )}
        </div>
      </div>
    </Main>
  );
};
